import apiClient from '../../api';
import { v4 as uuidv4 } from 'uuid';
import { isObj, isArr } from '@/utils';
import store from '..';
import initialState from '../initialState';

const trackingApi = apiClient.merchy.tracking;

/**
 * @description Is valid
 * @param tracking
 * @returns {boolean}
 */
const isValid = (tracking) => {
  const { activeAdvertiser, advertisers, advertisersMeta, programStatuses, activeTrafficNetwork, networkCredentials,
    trafficNetworks, trafficNetworkTypes, advertiserDomains, advertiserPrograms, advertiserProgramsMeta, comboTypes,
    programTags } = tracking;

  return (
    isObj(tracking) &&
    isObj(activeAdvertiser) &&
    isArr(advertisers) &&
    isObj(advertisersMeta) &&
    isArr(programStatuses) &&
    isObj(activeTrafficNetwork) &&
    isArr(networkCredentials) &&
    isArr(trafficNetworks) &&
    isArr(trafficNetworkTypes) &&
    isArr(advertiserDomains) &&
    isArr(advertiserPrograms) &&
    isObj(advertiserProgramsMeta) &&
    isArr(comboTypes) &&
    isArr(programTags)
  );
};

/**
 * @description Init state
 * @param initialState
 * @returns {*}
 */
const initState = (initialState) => {
  if (!isValid(initialState)) {
    throw Error('Invalid initial tracking state');
  }

  const { activeAdvertiser, activeProgram, advertisers, advertisersMeta, programStatuses, activeTrafficNetwork,
    networkCredentials, trafficNetworks, trafficNetworkTypes, advertiserDomains, advertiserPrograms,
    advertiserProgramsMeta, comboTypes, programTags } = initialState;
  return {
    activeAdvertiser,
    activeProgram,
    advertisers,
    advertisersMeta,
    programStatuses,
    activeTrafficNetwork,
    networkCredentials,
    trafficNetworks,
    trafficNetworkTypes,
    advertiserDomains,
    advertiserPrograms,
    advertiserProgramsMeta,
    comboTypes,
    programTags,
  };
};

/**
 * @description Getters
 * @type {*}
 */
export const getters = {
  activeAdvertiser: ({ activeAdvertiser }) => activeAdvertiser,
  advertisers: ({ advertisers }) => advertisers,
  advertisersMeta: ({ advertisersMeta }) => advertisersMeta,
  programStatuses: ({ programStatuses }) => programStatuses,
  activeTrafficNetwork: ({ activeTrafficNetwork }) => activeTrafficNetwork,
  networkCredentials: ({ networkCredentials }) => networkCredentials,
  trafficNetworks: ({ trafficNetworks }) => trafficNetworks,
  trafficNetworkTypes: ({ trafficNetworkTypes }) => trafficNetworkTypes,
  advertiserDomains: ({ advertiserDomains }) => advertiserDomains,
  advertiserPrograms: ({ advertiserPrograms }) => advertiserPrograms,
  advertiserProgramsMeta: ({ advertiserProgramsMeta }) => advertiserProgramsMeta,
  comboTypes: ({ comboTypes }) => comboTypes,
  programTags: ({ programTags }) => programTags,
};

/**
 * @description Handle alerts
 * @param message
 * @param alertType
 */
const handleAlerts = (message, alertType = 'success') => {
  const alert = {
    data: {
      message,
    },
  };

  store.dispatch('alerts/set', {
    data: alert,
    alertType,
  });
};

const actions = {
  // Create
  createAdvertiser: (_, advertiser) =>
    trackingApi.createAdvertiser(advertiser).then((res) => {
      handleAlerts('Advertiser created successfully.');
      return res.data.data;
    }),
  createDomain: ({ commit, state }, domain) =>
    trackingApi.createDomain(domain).then((res) => {
      handleAlerts('Domain created successfully.');

      const domainObj = domain.domain;
      const originalDomainURL = domainObj.domain.toLowerCase();
      const newDomain = res.data.data.find((domain) => domain.domain === originalDomainURL);
      newDomain.uuid = domainObj.uuid; // keep the uuid for proper animation

      const nextState = {
        ...state,
        advertiserDomains: [...state.advertiserDomains, newDomain],
      };

      commit('SET', nextState);

      return res.data.data;
    }),
  createProgram: ({ commit, state }, program) =>
    trackingApi.createProgram(program).then((res) => {
      handleAlerts('Program created successfully.');

      const nextState = {
        ...state,
        advertiserPrograms: res.data.data,
      };

      commit('SET', nextState);
      return res.data.data;
    }),

  // Update
  updateAdvertiser: ({ commit, state }, advertiser) =>
    trackingApi.updateAdvertiser(advertiser).then((res) => {
      handleAlerts('Advertiser updated successfully.');

      const nextState = {
        ...state,
        activeAdvertiser: res.data.data,
      };

      commit('SET', nextState);
      return res.data.data;
    }),
  updateDomain: ({ commit, state }, query) => {
    const uuid = query.domain.uuid; // keep the uuid

    return trackingApi.updateDomain(query).then((res) => {
      handleAlerts('Domain updated successfully.');

      const nextState = {
        ...state,
        advertiserDomains: state.advertiserDomains.map((domain) => {
          if (domain.id === query.domainId) {
            return {
              ...res.data.data,
              uuid,
            };
          }
          return domain;
        }),
      };

      commit('SET', nextState);
      return res.data.data;
    });
  },
  updateProgram: ({ commit, state }, query) =>
    trackingApi.updateProgram(query).then((res) => {
      handleAlerts('Program updated successfully.');

      const updatedProgram = res.data.data;

      const nextState = {
        ...state,
        advertiserPrograms: state.advertiserPrograms.map((program) => {
          if (program.id === query.program.id) {
            return updatedProgram;
          }
          return program;
        }),
      };

      commit('SET', nextState);
      return updatedProgram;
    }),

  // Get
  getAdvertiser: ({ commit, state }, id) =>
    trackingApi.getAdvertiser(id).then((res) => {
      const activeAdvertiser = res.data.data;
      const nextState = {
        ...state,
        activeAdvertiser,
      };

      commit('SET', nextState);
      return activeAdvertiser;
    }),
  getAdvertisers: ({ commit, state }, query) =>
    trackingApi.getAdvertisers(query).then((res) => {
      const { data, meta } = res.data;
      const advertisersData = {
        advertisers: data,
        advertisersMeta: meta,
      };
      const nextState = {
        ...state,
        ...advertisersData,
      };

      commit('SET', nextState);
      return advertisersData;
    }),
  getDomains: ({ commit, state }, id) =>
    trackingApi.getDomains(id).then((res) => {
      const domains = res.data.data;
      const nextState = {
        ...state,
        advertiserDomains: domains.map((domain) => ({
          ...domain,
          uuid: domain.uuid || uuidv4(), // uuid is used to set key for domains to properly animate them
        })),
      };

      commit('SET', nextState);
      return domains;
    }),
  checkIfCountryIsLinkedToDomain: (_, query) =>
    trackingApi.checkIfCountryIsLinkedToDomain(query).then((res) => {
      return res.data.data;
    }),
  getProgram: ({ commit, state }, query) =>
    trackingApi.getProgram(query).then((res) => {
      const activeProgram = res.data.data;
      const nextState = {
        ...state,
        activeProgram,
      };

      commit('SET', nextState);
      return activeProgram;
    }),
  getPrograms: ({ commit, state }, query) =>
    trackingApi.getPrograms(query).then((res) => {
      const { data } = res.data;
      const programsData = {
        advertiserPrograms: data,
      };
      const nextState = {
        ...state,
        ...programsData,
      };

      commit('SET', nextState);
      return data;
    }),
  getProgramStatuses: ({ commit, state }) =>
    trackingApi.getProgramStatuses().then((res) => {
      const nextState = {
        ...state,
        programStatuses: res.data.data,
      };

      commit('SET', nextState);
      return res;
    }),
  createTrafficNetwork: (_, data) =>
    trackingApi.createTrafficNetwork(data).then((res) => {
      handleAlerts('Network created successfully.');
      return res.data.data;
    }),
  updateTrafficNetwork: ({ commit, state }, data) =>
    trackingApi.updateTrafficNetwork(data).then((res) => {
      handleAlerts('Network updated successfully.');
      const nextState = {
        ...state,
        activeTrafficNetwork: res.data.data,
      };
      commit('SET', nextState);
      return res.data.data;
    }),
  getNetworkCredentials: ({ commit, state }, networkId) =>
    trackingApi.getNetworkCredentials(networkId).then((res) => {
      const nextState = {
        ...state,
        networkCredentials: res.data.data,
      };
      commit('SET', nextState);
      return res.data.data;
    }),
  createNetworkCredentials: (_, credentials) =>
    trackingApi.createNetworkCredentials(credentials).then((res) => {
      handleAlerts('Credentials created successfully.');
      return res.data.data;
    }),
  updateNetworkCredentials: (_, credentials) =>
    trackingApi.updateNetworkCredentials(credentials).then((res) => {
      handleAlerts('Credentials updated successfully.');
      return res.data.data;
    }),
  getTrafficNetwork: ({ commit, state }, id) =>
    trackingApi.getTrafficNetwork(id).then((res) => {
      const activeTrafficNetwork = res.data.data;
      const nextState = {
        ...state,
        activeTrafficNetwork,
      };

      commit('SET', nextState);
      return activeTrafficNetwork;
    }),
  getTrafficNetworks: ({ commit, state }, query) =>
    trackingApi.getTrafficNetworks(query).then((res) => {
      const { data, meta } = res.data;
      const nextState = {
        ...state,
        trafficNetworks: data,
      };

      commit('SET', nextState);
      return { data, meta };
    }),
  getTrafficNetworkTypes: ({ commit, state }) =>
    trackingApi.getTrafficNetworkTypes().then((res) => {
      const nextState = {
        ...state,
        trafficNetworkTypes: res.data.data,
      };

      commit('SET', nextState);
      return res;
    }),
  getProgramTags: ({ commit, state }) =>
    trackingApi.getProgramTags().then((res) => {
      const nextState = {
        ...state,
        programTags: res.data.data,
      };
      commit('SET', nextState);
      return res.data.data;
    }),
  getComboTypes: ({ commit, state }) =>
    trackingApi.getComboTypes().then((res) => {
      const nextState = {
        ...state,
        comboTypes: res.data.data,
      };

      commit('SET', nextState);
      return res;
    }),
  checkIfThereIsActiveProgram: (_, query) =>
    trackingApi.checkIfThereIsActiveProgram(query).then((res) => {
      return res.data.data;
    }),
  getFilterStatuses: (_) =>
    trackingApi.getFilterStatuses().then((res) => {
      return res.data.data;
    }),
  getPreviewLink: (_, query) =>
    trackingApi.getPreviewLink(query).then((res) => {
      return res.data.data;
    }),

  // Delete
  deleteNetworkCredentials: ({ commit, state }, query) =>
    trackingApi.deleteNetworkCredentials(query).then((res) => {
      const nextState = {
        ...state,
        networkCredentials: state.networkCredentials.filter((c) => c.id !== query.credential_id),
      };
      commit('SET', nextState);
      handleAlerts('Credentials deleted successfully.');
      return res.data.data;
    }),
  deleteDomain: ({ commit, state }, query) =>
    trackingApi.deleteDomain(query).then((res) => {
      const nextState = {
        ...state,
        advertiserDomains: state.advertiserDomains.filter((domain) => domain.id !== query.domainId),
      };
      commit('SET', nextState);
      handleAlerts('Domain removed successfully.');
      return res.data.data;
    }),

  // Upload
  bulkUploadPrograms: (_, query) =>
    trackingApi.bulkUploadPrograms(query).then((res) => {
      handleAlerts('Programs uploaded successfully.');
      return res.data.data;
    })
    .catch((err) => {
      return Promise.reject(err.response ? err.response.data : null);
    }),

  // Export
  exportClickyConfig: () =>
    trackingApi.exportClickyConfig().then((res) => {
      handleAlerts('Clicky config export started. The configuration files are being regenerated.');
      return res.data.data;
    }),

  // Clear
  clearDomains: ({ commit, state }) => {
    const nextState = {
      ...state,
      advertiserDomains: [],
    };
    commit('SET', nextState);
    return [];
  },

  // Setters
  setActiveAdvertiser: ({ commit, state }, advertiser) => {
    const nextState = {
      ...state,
      activeAdvertiser: advertiser,
    };

    commit('SET', nextState);
    return advertiser;
  },
  setActiveTrafficNetwork: ({ commit, state }, network) => {
    const nextState = {
      ...state,
      activeTrafficNetwork: network,
    };

    commit('SET', nextState);
    return network;
  },
  setActiveProgram: ({ commit, state }, program) => {
    const nextState = {
      ...state,
      activeProgram: program,
    };

    commit('SET', nextState);
    return program;
  },

  // Store actions
  reset: ({ commit }) => commit('SET', initialState.tracking),
  set: ({ commit }, tracking) => {
    commit('SET', tracking);
  },
};

const mutations = {
  SET(state, tracking) {
    /* eslint-disable no-param-reassign */
    state.advertisers = tracking.advertisers;
    state.activeProgram = tracking.activeProgram;
    state.advertisersMeta = tracking.advertisersMeta;
    state.activeAdvertiser = tracking.activeAdvertiser;
    state.programStatuses = tracking.programStatuses;
    state.activeTrafficNetwork = tracking.activeTrafficNetwork;
    state.networkCredentials = tracking.networkCredentials;
    state.trafficNetworks = tracking.trafficNetworks;
    state.trafficNetworkTypes = tracking.trafficNetworkTypes;
    state.advertiserDomains = tracking.advertiserDomains;
    state.advertiserPrograms = tracking.advertiserPrograms;
    state.advertiserProgramsMeta = tracking.advertiserProgramsMeta;
    state.comboTypes = tracking.comboTypes;
    state.programTags = tracking.programTags;
  },
};

export default (initialState) => ({
  namespaced: true,
  state: initState(initialState),
  getters,
  actions,
  mutations,
});
